import { ProfilePicture } from '@components/profilePicture';
import { cn } from '@utils/helpers';
import { AtSignIcon, PhoneIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useFragment } from 'react-relay';
import { fundsManagerFragment } from './FundsManager.relay';
import { useFundsManagerHover } from './hooks';
import type { FundsManagerProps } from './types';

export const FundsManager: React.FC<FundsManagerProps> = ({ component }) => {
  const data = useFragment(fundsManagerFragment, component);
  const { setWrapperRef, setBackgroundRef, setEmailParentRef, setEmailTextRef, setPhoneParentRef, setPhoneTextRef, startMeasurement, onMouseEnter, onMouseLeave } = useFundsManagerHover();

  useEffect(() => {
    startMeasurement();
  }, [startMeasurement]);

  return (
    <div
      ref={setWrapperRef}
      className="FundsManager relative z-200 w-[410px] max-[1100px]:w-full [&.is-extended_.FundsManager-Meta-head-item.shrink]:max-w-[400px] [&.is-extended_.FundsManager-Meta-head-text]:opacity-100"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="FundsManager-Meta-head relative z-40 mt-[2em]">
        <a
          ref={setEmailParentRef}
          href={data.email ? `mailto:${data.email}` : undefined}
          className={`FundsManager-Meta-head-item FundsManager-Meta-head-mail absolute left-1/2 top-1/2 z-50 ml-[-135px] mt-[-60px] flex items-center justify-start whitespace-nowrap border-b border-black px-0 pt-[5px] pb-0 pr-[30px] leading-none text-[#888] no-underline transition-[transform,max-width] duration-700 ease-in-out [&.shrink]:max-w-40 ${!data.email ? 'is-disabled' : ''}`}
        >
          <AtSignIcon className={cn('w-10 h-10 p-2 shrink-0', data.email ? 'text-accent' : 'text-neutral-500')} strokeWidth={1} />
          <span className="FundsManager-Meta-head-text inline-block overflow-hidden leading-normal opacity-0 transition-opacity duration-500 max-[720px]:hidden" ref={setEmailTextRef}>
            {data.email}
          </span>
        </a>

        <a
          ref={setPhoneParentRef}
          href={data.phone ? `tel:${data.phone}` : undefined}
          className={`FundsManager-Meta-head-item FundsManager-Meta-head-phone absolute left-1/2 top-1/2 z-50 ml-[-135px] mt-[-5px] flex items-center justify-start whitespace-nowrap border-b border-black px-0 pt-[5px] pb-0 pr-[30px] leading-none text-[#888] no-underline transition-[transform,max-width] duration-700 ease-in-out [&.shrink]:max-w-40 ${!data.phone ? 'is-disabled' : ''}`}
        >
          <PhoneIcon className={cn('w-10 h-10 p-2 shrink-0', data.phone ? 'text-accent' : 'text-gray-500')} strokeWidth={1} />
          <span className="FundsManager-Meta-head-text inline-block overflow-hidden leading-normal opacity-0 transition-opacity duration-500 max-[720px]:hidden" ref={setPhoneTextRef}>
            {data.phone}
          </span>
        </a>

        <ProfilePicture picture={data.picture} managername={data.managername} />

        <div
          ref={setBackgroundRef}
          className="FundsManager-background absolute left-0 top-0 z-10 h-[520px] w-[520px] ml-[-170px] mt-[-170px] rounded-[260px] border border-[#e8e8e8] bg-[rgba(255,255,255,0.9)] opacity-0 transition-[transform,opacity] duration-700 ease-in-out transform-[scale(0)] [&.is-visible]:opacity-100 [&.is-visible]:transform-[scale(1)] max-[1100px]:ml-[-70px] max-[720px]:hidden"
        />
      </div>
      <div className="FundsManager-Meta-body">
        <div className="FundsManager-Meta-body-name h4 relative z-50 mx-auto mt-[15px] mb-0.5 block max-w-[300px] align-text-bottom text-center leading-5 no-underline">
          {data.managername}
          {data.managercompany ? (
            <span>
              <br />({data.managercompany})
            </span>
          ) : null}
        </div>
        <small className="FundsManager-Meta-body-adviserText block align-text-bottom text-center text-sm leading-[22px] text-black no-underline">{data.advisortext}</small>
      </div>
    </div>
  );
};
